import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Search,
  Printer,
  Calendar,
  Filter,
  Layers,
  CheckCircle,
  LayoutList,
  ArrowUpCircle,
  ArrowDownCircle,
  X,
  ShieldAlert,
  Clock,
  Disc,
} from "lucide-react";

// Hooks
import { useProductionContext } from "../contexts/ProductionContext";
import { useProductionFilter } from "../hooks/useProductionFilter";
import { useProductionGrouping } from "../hooks/useProductionGrouping";

// Services e Config
import { productionService } from "../services/productionService";
import { logAction, MODULES, getSafeUser } from "../services/logService";
import {
  PRODUCTION_STATUS_CONFIG,
  KANBAN_ORDER,
  isStatusAguardandoInjecao,
  isStatusFaseCeraMolde,
} from "../config/productionStatuses";
import { formatProductionTicket } from "../utils/printFormatter";
import { getBusinessDaysDiff } from "../utils/formatters";
import { cleanSku, getBaseSku, isModeloComMedida } from "../utils/skuUtils";
import { avaliarDisponibilidadeMolde } from "../utils/moldRingUtils";

// Quality Control Configuration & Services
import { isMonitored } from "../config/monitoredStatuses";
import { desvioEventosService } from "../services/desvioEventosService";

// Componentes
import ProductionListView from "../components/production/ProductionListView";
import AgeChart from "../components/dashboard/AgeChart";
import TextModal from "../components/modals/TextModal";
import ProductionConversionModal from "../components/modals/ProductionConversionModal";
import DesvioMotivoModal from "../components/production/DesvioMotivoModal";
import DesviosDashSubTab from "../components/production/DesviosDashSubTab";
import PrazosControleSubTab from "../components/production/PrazosControleSubTab";
import SeparacaoMoldesSubTab from "../components/production/SeparacaoMoldesSubTab";
import { useCatalogContext } from "../contexts/CatalogContext";

export default function ProductionTab({ user, findCatalogItem }) {
  // 1. Dados (Hooks)
  const { orders, loading, setOrders } = useProductionContext();
  const { isMoldeSku } = useCatalogContext();

  // 2. Estados Locais (TODOS NO TOPO PARA EVITAR ERROS)
  const [activeSubTab, setActiveSubTab] = useState(() => {
    return localStorage.getItem("production_active_subtab") || "painel";
  });

  const [desvioModalOpen, setDesvioModalOpen] = useState(false);
  const [desvioStatusDestino, setDesvioStatusDestino] = useState(null);
  const [desvioOrdersList, setDesvioOrdersList] = useState([]);

  const [filterText, setFilterText] = useState("");
  const [activeStatusFilter, setActiveStatusFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("status");
  const [typeFilter, setTypeFilter] = useState("all");

  // Estado para Filtro de Idade (Vindo do Gráfico)
  const [ageFilter, setAgeFilter] = useState(null);

  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [editingOrder, setEditingOrder] = useState(null);
  const [printContent, setPrintContent] = useState(null);

  // Contador de itens com molde de borracha ativo na produção (etapas de cera/injeção/árvore)
  const borrachaCount = useMemo(() => {
    return orders.filter(order => {
      if (!isStatusFaseCeraMolde(order.status)) return false;
      const cat = findCatalogItem ? findCatalogItem(order.sku) : null;
      const cleanPai = getBaseSku(cleanSku(order.sku));
      const hasBase = Boolean(
        order.tem_molde ||
        order.full_data?.tem_molde ||
        order.specs?.tem_molde ||
        cat?.tem_molde ||
        cat?.full_data?.tem_molde ||
        cat?.full_data?.processo_fabricacao === "borracha" ||
        (isMoldeSku && (isMoldeSku(order.sku, { order }) || isMoldeSku(cleanPai, { order })))
      );
      if (!hasBase) return false;

      // Validação de aro para anéis e alianças
      const moldConfig = cat?.full_data?.config_borracha || cat?.config_borracha;
      if (moldConfig && isModeloComMedida(moldConfig.tipo_modelo || cat?.tipo_modelo)) {
        return avaliarDisponibilidadeMolde(order, moldConfig, { catItem: cat }).disponivel;
      }
      return true;
    }).length;
  }, [orders, findCatalogItem, isMoldeSku]);

  // Save active subtab to localStorage
  useEffect(() => {
    localStorage.setItem("production_active_subtab", activeSubTab);
  }, [activeSubTab]);

  // 3. Processamento (Filtros em Cascata)

  // A: Filtro de Texto (Busca)
  const baseFilteredOrders = useProductionFilter(
    orders,
    filterText,
    "all",
    findCatalogItem
  );

  // 3. Processamento (Filtros em Cascata Envelopados)
  const finalFilteredOrders = useMemo(() => {
    // A: Filtro de Tipo Especial (Middleware)
    const typeFilteredOrders = baseFilteredOrders.filter((order) => {
      if (typeFilter === "all") return true;
      if (typeFilter === "stock") return order.fromStock === true;
      if (typeFilter === "pe") return order.isInterceptedPE === true; // Flag correta do banco
      if (typeFilter === "printed") return order.printed === true;
      if (typeFilter === "not_printed") return !order.printed;
      if (typeFilter === "transit") return !!order.transit_status; // Filtro de Transito
      if (typeFilter === "borracha") {
        if (!isStatusFaseCeraMolde(order.status)) return false;
        const cat = findCatalogItem ? findCatalogItem(order.sku) : null;
        const cleanPai = getBaseSku(cleanSku(order.sku));
        const hasBase = Boolean(
          order.tem_molde ||
          order.full_data?.tem_molde ||
          order.specs?.tem_molde ||
          cat?.tem_molde ||
          cat?.full_data?.tem_molde ||
          cat?.full_data?.processo_fabricacao === "borracha" ||
          (isMoldeSku && (isMoldeSku(order.sku, { order }) || isMoldeSku(cleanPai, { order })))
        );
        if (!hasBase) return false;

        const moldConfig = cat?.full_data?.config_borracha || cat?.config_borracha;
        if (moldConfig && isModeloComMedida(moldConfig.tipo_modelo || cat?.tipo_modelo)) {
          return avaliarDisponibilidadeMolde(order, moldConfig, { catItem: cat }).disponivel;
        }
        return true;
      }
      return true;
    });

    // B: Filtro de Data/Idade (Usa o resultado do anterior)
    return typeFilteredOrders.filter((order) => {
      if (!ageFilter) return true;

      const created = order.customCreatedAt || order.createdAt;
      const diffDays = getBusinessDaysDiff(created); // <--- A MÁGICA É AQUI

      if (ageFilter.min !== undefined && diffDays < ageFilter.min) return false;
      if (ageFilter.max !== undefined && diffDays > ageFilter.max) return false;

      return true;
    });
  }, [baseFilteredOrders, typeFilter, ageFilter]);

  const groupedOrders = useProductionGrouping(finalFilteredOrders, groupBy);

  // 4. Memoização Final da Aba Visual para não quebrar o React.memo dos filhos
  const memoizedGroupedOrdersForView = useMemo(() => {
    if (activeStatusFilter === "all") return groupedOrders;
    return {
      [activeStatusFilter]: groupedOrders[activeStatusFilter] || [],
    };
  }, [groupedOrders, activeStatusFilter]);

  // --- HANDLERS COM LOGS ---

  // 1. TRÂNSITO EM MASSA
  const handleBulkTransit = useCallback(async (direction) => {
    const label =
      direction === "subindo" ? "SUBINDO (Escritório)" : "DESCENDO (Fábrica)";

    if (
      !window.confirm(
        `Confirmar envio de ${selectedOrders.size} itens para: ${label}?`
      )
    ) {
      return;
    }

    let previousOrders = [];
    if (setOrders) {
      setOrders((prev) => {
        previousOrders = [...prev];
        return prev.map((order) => {
          if (selectedOrders.has(order.id)) {
            return {
              ...order,
              transit_status: direction,
              updated: new Date().toISOString()
            };
          }
          return order;
        });
      });
    }

    try {
      const updatePromises = Array.from(selectedOrders).map(async (orderId) => {
        const order = orders.find((o) => o.id === orderId);
        if (!order) return;

        await productionService.toggleTransit(order.id, direction, user?.name);

        // LOG
        await logAction(
          getSafeUser(user),
          MODULES.PRODUCAO,
          "TRANSITO_MASSA",
          `Lote: Marcou ${label} - Item ${order.sku}`,
          { itemId: order.id, sku: order.sku, direction }
        );
      });

      await Promise.all(updatePromises);
      setSelectedOrders(new Set());
      alert(`Trânsito atualizado para ${selectedOrders.size} itens!`);
    } catch (error) {
      if (setOrders && previousOrders.length > 0) setOrders(previousOrders);
      console.error("Erro no trânsito em massa:", error);
      alert("Erro ao atualizar alguns itens. Alteração desfeita.");
    }
  }, [selectedOrders, orders, user, setOrders]);

  // 2. MOVER STATUS INDIVIDUAL
  const handleMoveStatus = useCallback(async (orderId, newStatus) => {
    const order = orders.find((o) => String(o.id) === String(orderId));
    if (!order) return;

    const oldStatus = order.status;

    let previousOrders = [];
    if (setOrders) {
      setOrders((prev) => {
        previousOrders = [...prev];
        return prev.map((o) =>
          String(o.id) === String(orderId)
            ? {
                ...o,
                status: newStatus,
                production_step: newStatus,
                full_data: {
                  ...(o.full_data || {}),
                  status: newStatus,
                },
                updated: new Date().toISOString(),
              }
            : o
        );
      });
    }

    try {
      await productionService.updateStatus(
        orderId,
        newStatus,
        oldStatus,
        user?.name
      );

      // LOG
      await logAction(
        getSafeUser(user),
        MODULES.PRODUCAO,
        "MOVER_STATUS",
        `Moveu ${order.sku} de "${oldStatus}" para "${newStatus}"`,
        { itemId: orderId, oldStatus, newStatus }
      );

      // FASE 3: Incrementar estatística de transição
      desvioEventosService.updateFluxoResumo(oldStatus, newStatus);

      // Se for monitorado, abre o modal de justificativa
      if (isMonitored(newStatus)) {
        setDesvioOrdersList([{ ...order, status: oldStatus }]);
        setDesvioStatusDestino(newStatus);
        setDesvioModalOpen(true);
      }
    } catch (error) {
      if (setOrders && previousOrders.length > 0) setOrders(previousOrders);
      alert("Erro ao mover: " + error.message);
    }
  }, [orders, user, setOrders]);

  // 3. EXCLUIR PEDIDO
  const handleDeleteOrder = useCallback(async (order) => {
    if (!window.confirm(`Excluir pedido ${order.order?.number || order.sku}?`))
      return;

    let previousOrders = [];
    if (setOrders) {
      setOrders((prev) => {
        previousOrders = [...prev];
        return prev.filter((o) => o.id !== order.id);
      });
    }

    try {
      await productionService.deleteOrder(order.id);

      // LOG
      await logAction(
        getSafeUser(user),
        MODULES.PRODUCAO,
        "EXCLUIR_PEDIDO",
        `Excluiu definitivamente o pedido ${order.sku}`,
        { itemId: order.id, sku: order.sku, data: order }
      );
    } catch (error) {
      if (setOrders && previousOrders.length > 0) setOrders(previousOrders);
      alert("Erro ao excluir: " + error.message);
    }
  }, [user, setOrders]);

  // 4. TOGGLE TRÂNSITO INDIVIDUAL
  const handleToggleTransit = useCallback(async (order, direction) => {
    const newStatus = order.transit_status === direction ? null : direction;

    let previousOrders = [];
    if (setOrders) {
      setOrders((prev) => {
        previousOrders = [...prev];
        return prev.map((o) =>
          o.id === order.id
            ? { ...o, transit_status: newStatus, updated: new Date().toISOString() }
            : o
        );
      });
    }

    try {
      await productionService.toggleTransit(order.id, newStatus, user?.name);

      // LOG
      const actionText = newStatus
        ? `Marcou trânsito: ${newStatus === "subindo" ? "SUBINDO" : "DESCENDO"}`
        : "Removeu marcação de trânsito";

      await logAction(
        getSafeUser(user),
        MODULES.PRODUCAO,
        "TRANSITO",
        `${actionText} - Item ${order.sku}`,
        { itemId: order.id, newStatus }
      );
    } catch (error) {
      if (setOrders && previousOrders.length > 0) setOrders(previousOrders);
      alert("Erro no trânsito: " + error.message);
    }
  }, [user, setOrders]);

  // 5. TOGGLE PE (NOVO - DAR BAIXA NA URGÊNCIA)
  const handleTogglePE = useCallback(async (order) => {
    if (
      !window.confirm(
        `Deseja remover a marcação de PRONTA ENTREGA deste item?\n\nEle se tornará um pedido comum na lista.`
      )
    )
      return;

    let previousOrders = [];
    if (setOrders) {
      setOrders((prev) => {
        previousOrders = [...prev];
        return prev.map((o) =>
          o.id === order.id
            ? { ...o, isInterceptedPE: !order.isInterceptedPE, updated: new Date().toISOString() }
            : o
        );
      });
    }

    try {
      await productionService.togglePE(
        order.id,
        order.isInterceptedPE,
        user?.name
      );

      // LOG
      await logAction(
        getSafeUser(user),
        MODULES.PRODUCAO,
        "BAIXA_PE",
        `Removeu marcação PE do item ${order.sku}`,
        { itemId: order.id }
      );
    } catch (error) {
      if (setOrders && previousOrders.length > 0) setOrders(previousOrders);
      alert("Erro ao alterar PE: " + error.message);
    }
  }, [user, setOrders]);

  // 6. IMPRESSÃO EM LOTE (CORRIGIDA COM SANITIZAÇÃO)
  const handleBatchPrint = useCallback(async () => {
    if (selectedOrders.size === 0) return;

    // Pega itens crus
    const rawItems = orders.filter((o) => selectedOrders.has(o.id));

    // Sanitiza datas para evitar erro .split
    const safeItems = rawItems.map((item) => {
      let rawDate = item.dateStr || item.createdAt || item.customCreatedAt;
      let safeString = "";

      try {
        if (rawDate && typeof rawDate.toDate === "function") {
          safeString = rawDate.toDate().toLocaleString("pt-BR");
        } else if (rawDate instanceof Date) {
          safeString = rawDate.toLocaleString("pt-BR");
        } else if (typeof rawDate === "string") {
          if (rawDate.includes("T")) {
            safeString = new Date(rawDate).toLocaleString("pt-BR");
          } else {
            safeString = rawDate;
          }
        } else {
          safeString = new Date().toLocaleString("pt-BR");
        }
      } catch (e) {
        safeString = new Date().toLocaleString("pt-BR");
      }

      return { ...item, dateStr: safeString };
    });

    const text = formatProductionTicket(safeItems);
    setPrintContent(text);

    await productionService.markBatchAsPrinted(Array.from(selectedOrders), user?.name);

    // LOG
    await logAction(
      getSafeUser(user),
      MODULES.PRODUCAO,
      "IMPRIMIR_LOTE",
      `Gerou ticket de produção para ${rawItems.length} itens`,
      { itemIds: Array.from(selectedOrders) }
    );

    setSelectedOrders(new Set());
  }, [orders, selectedOrders, user]);

  // 7. MOVER EM MASSA
  const handleBatchMove = useCallback(async (newStatus) => {
    if (!newStatus) return;
    const label = PRODUCTION_STATUS_CONFIG[newStatus]?.label || newStatus;
    const confirmMessage = `Tem certeza que deseja mover ${selectedOrders.size} pedidos para "${label}"?`;
    if (!window.confirm(confirmMessage)) return;

    const ordersToMove = orders.filter((o) => selectedOrders.has(o.id));

    let previousOrders = [];
    if (setOrders) {
      setOrders((prev) => {
        previousOrders = [...prev];
        return prev.map((order) => {
          if (selectedOrders.has(order.id)) {
            return {
              ...order,
              status: newStatus,
              updated: new Date().toISOString()
            };
          }
          return order;
        });
      });
    }

    try {
      const promises = ordersToMove.map(async (order) => {
        await productionService.updateStatus(
          order.id,
          newStatus,
          order.status,
          user?.name || "Admin"
        );

        // LOG INDIVIDUAL
        await logAction(
          getSafeUser(user),
          MODULES.PRODUCAO,
          "MOVER_MASSA",
          `Lote: Moveu ${order.sku} para ${label}`,
          { itemId: order.id, oldStatus: order.status, newStatus }
        );

        // FASE 3: Incrementar estatística de transição
        desvioEventosService.updateFluxoResumo(order.status, newStatus);
      });

      await Promise.all(promises);
      setSelectedOrders(new Set());

      // Se o status de destino for monitorado, abre o modal de lote
      if (isMonitored(newStatus)) {
        setDesvioOrdersList(ordersToMove);
        setDesvioStatusDestino(newStatus);
        setDesvioModalOpen(true);
      } else {
        alert("Movimentação em massa concluída!");
      }
    } catch (error) {
      if (setOrders && previousOrders.length > 0) setOrders(previousOrders);
      console.error("Erro no lote:", error);
      alert("Erro ao mover alguns pedidos. Verifique o console.");
    }
  }, [orders, selectedOrders, user, setOrders]);

  // 8. SALVAR SPECS (EDIÇÃO)
  const handleSaveSpecs = useCallback(async (data) => {
    try {
      await productionService.updateSpecs(
        data.id,
        data.specs,
        data.exception,
        data.sku,
        user?.name
      );

      // LOG
      await logAction(
        getSafeUser(user),
        MODULES.PRODUCAO,
        "EDITAR_SPECS",
        `Editou especificações/exceção do item ${data.id}`,
        { itemId: data.id, newSpecs: data.specs, newException: data.exception }
      );

      setEditingOrder(null);
    } catch (error) {
      alert("Erro ao salvar specs: " + error.message);
    }
  }, [user]);

  // 9. ATUALIZAR DATA
  const handleUpdateDate = useCallback(async (orderId, newDate) => {
    let previousOrders = [];
    if (setOrders) {
      setOrders((prev) => {
        previousOrders = [...prev];
        return prev.map((o) =>
          o.id === orderId
            ? { ...o, customCreatedAt: newDate, updated: new Date().toISOString() }
            : o
        );
      });
    }

    try {
      await productionService.updateOrderField(
        orderId,
        "customCreatedAt",
        newDate,
        user?.name || "Admin"
      );

      // LOG
      await logAction(
        getSafeUser(user),
        MODULES.PRODUCAO,
        "ALTERAR_DATA",
        `Alterou data do pedido ${orderId} para ${newDate}`,
        { itemId: orderId, newDate }
      );
    } catch (error) {
      if (setOrders && previousOrders.length > 0) setOrders(previousOrders);
      alert("Erro ao atualizar data: " + error.message);
    }
  }, [user, setOrders]);

  // 10. CONTROLE DE QUALIDADE (DESVIOS)
  const handleSaveDesvios = useCallback(async (eventos) => {
    try {
      if (eventos.length > 1) {
        await desvioEventosService.createEventosLote(eventos, user?.id);
      } else if (eventos.length === 1) {
        await desvioEventosService.createEvento({
          ...eventos[0],
          registrado_por: user?.id,
        });
      }
      setDesvioModalOpen(false);
      setDesvioOrdersList([]);
      setDesvioStatusDestino(null);
    } catch (e) {
      console.error("Erro ao salvar desvio(s):", e);
      alert("Erro ao gravar justificativa do desvio: " + e.message);
    }
  }, [user]);

  const handleCloseDesvioModal = useCallback(() => {
    // Dispensar salva com "Não Informado" automaticamente
    const eventos = desvioOrdersList.map((order) => ({
      status_destino: desvioStatusDestino,
      order_id: order.id,
      sku: order.sku,
      order_number: order.order?.number || "AVULSO",
      status_anterior: order.status || "",
      problema_id: "",
      observacoes: "",
      preenchido: false,
    }));
    
    handleSaveDesvios(eventos);
  }, [desvioOrdersList, desvioStatusDestino, handleSaveDesvios]);

  // --- OUTROS HANDLERS DE UI ---
  const handleChartClick = useCallback((range) => {
    if (ageFilter && ageFilter.label === range.label) {
      setAgeFilter(null);
    } else {
      setAgeFilter(range);
    }
  }, [ageFilter]);

  const toggleSelect = useCallback((id) => {
    setSelectedOrders((prevSet) => {
      const newSet = new Set(prevSet);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  }, []);

  if (loading)
    return (
      <div className="p-10 text-center text-slate-500">
        Carregando produção...
      </div>
    );

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] w-full overflow-hidden bg-slate-50">
      {/* --- NAVEGAÇÃO DE SUB-ABAS --- */}
      <div className="flex px-6 pt-4 border-b border-slate-200 bg-white gap-2 shrink-0 overflow-x-auto [&::-webkit-scrollbar]:hidden z-20">
        <button
          onClick={() => setActiveSubTab("painel")}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap cursor-pointer ${
            activeSubTab === "painel"
              ? "border-slate-800 text-slate-800"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Layers size={15} /> Fluxo de Produção
        </button>

        <button
          onClick={() => setActiveSubTab("separacao_moldes")}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap cursor-pointer ${
            activeSubTab === "separacao_moldes"
              ? "border-amber-600 text-amber-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Disc size={15} className={activeSubTab === "separacao_moldes" ? "text-amber-600 animate-spin-slow" : ""} />
          <span>Separação (Moldes)</span>
          {borrachaCount > 0 && (
            <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 text-[10px] rounded-full font-black border border-amber-300">
              {borrachaCount}
            </span>
          )}
        </button>

        <button
          onClick={() => setActiveSubTab("prazos")}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap cursor-pointer ${
            activeSubTab === "prazos"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <Clock size={15} /> Previsão & Controle de Prazos
        </button>

        <button
          onClick={() => setActiveSubTab("qualidade")}
          className={`flex items-center gap-2 px-4 py-2 border-b-2 font-bold text-xs transition-all uppercase tracking-wider whitespace-nowrap cursor-pointer ${
            activeSubTab === "qualidade"
              ? "border-pink-600 text-pink-600"
              : "border-transparent text-slate-500 hover:text-slate-700"
          }`}
        >
          <ShieldAlert size={15} /> Controle de Qualidade (Desvios)
        </button>
      </div>

      {/* --- CONTEÚDO DAS SUB-ABAS --- */}
      <div className="flex-1 overflow-hidden min-h-0 relative">
        {/* MODAL DE CONTROLE DE QUALIDADE (CAUSAS) */}
        <DesvioMotivoModal
          isOpen={desvioModalOpen}
          onClose={handleCloseDesvioModal}
          statusDestino={desvioStatusDestino}
          ordersList={desvioOrdersList}
          onSave={handleSaveDesvios}
        />

        {/* --- MODAIS EXISTENTES --- */}
        {printContent && (
          <TextModal
            content={printContent}
            onClose={() => setPrintContent(null)}
          />
        )}
        {editingOrder && (
          <ProductionConversionModal
            isOpen={!!editingOrder}
            isEditing={true}
            reservation={editingOrder}
            inventory={[]}
            onClose={() => setEditingOrder(null)}
            onConfirm={handleSaveSpecs}
            findCatalogItem={findCatalogItem}
          />
        )}

        {activeSubTab === "qualidade" ? (
          <DesviosDashSubTab user={user} findCatalogItem={findCatalogItem} />
        ) : activeSubTab === "prazos" ? (
          <PrazosControleSubTab
            user={user}
            orders={orders}
            findCatalogItem={findCatalogItem}
            onMoveStatus={handleMoveStatus}
          />
        ) : activeSubTab === "separacao_moldes" ? (
          <SeparacaoMoldesSubTab
            user={user}
            orders={orders}
            findCatalogItem={findCatalogItem}
            onMoveStatus={handleMoveStatus}
            isMoldeSku={isMoldeSku}
          />
        ) : (
          <div className="flex flex-col md:flex-row h-full w-full overflow-hidden">
            {/* --- SIDEBAR LATERAL --- */}
            <aside className="hidden md:flex w-64 bg-white border-r border-slate-200 flex-col shrink-0 z-10 shadow-sm overflow-hidden h-full">
              <div className="p-4 border-b border-slate-100">
                <h2 className="font-bold text-slate-700 flex items-center gap-2">
                  <Layers size={18} /> Processos
                </h2>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar">
                <button
                  onClick={() => setActiveStatusFilter("all")}
                  className={`w-full text-left px-3 py-2 rounded-lg text-xs font-bold transition-all flex justify-between items-center ${
                    activeStatusFilter === "all"
                      ? "bg-slate-800 text-white shadow-md"
                      : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <span>VISÃO GERAL</span>
                  <span className="bg-white/20 px-1.5 rounded text-[10px]">
                    {finalFilteredOrders.length}
                  </span>
                </button>

                <div className="h-px bg-slate-200 my-2"></div>

                {KANBAN_ORDER.map((statusId) => {
                  const config = PRODUCTION_STATUS_CONFIG[statusId];
                  const count = groupedOrders[statusId]?.length || 0;
                  const isActive = activeStatusFilter === statusId;
                  const colorClass = config.color || "bg-slate-500";

                  return (
                    <button
                      key={statusId}
                      onClick={() =>
                        setActiveStatusFilter(isActive ? "all" : statusId)
                      }
                      className={`w-full text-left px-3 py-2.5 rounded-lg text-xs font-bold transition-all flex justify-between items-center border border-transparent ${
                        isActive
                          ? `shadow-md ring-2 ring-offset-1 ring-slate-300 ${colorClass} text-white`
                          : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      <span className="truncate">{config.label}</span>
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] ${
                          isActive
                            ? "bg-white/30 text-white"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </aside>

            {/* --- ÁREA PRINCIPAL --- */}
            <main className="flex-1 flex flex-col min-w-0 overflow-hidden relative h-full">
              {/* MENU MOBILE */}
              <div className="md:hidden bg-slate-100 border-b border-slate-200 p-3 shrink-0 z-20">
                <label className="text-[10px] font-bold text-slate-500 uppercase mb-1 flex items-center gap-1">
                  <Layers size={12} /> Filtrar Processo:
                </label>
                <div className="relative">
                  <select
                    value={activeStatusFilter}
                    onChange={(e) => setActiveStatusFilter(e.target.value)}
                    className="w-full appearance-none bg-white border border-slate-300 text-slate-800 text-sm font-bold rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-purple-500 shadow-sm"
                  >
                    <option value="all">
                      VISÃO GERAL ({finalFilteredOrders.length})
                    </option>
                    {KANBAN_ORDER.map((statusId) => (
                      <option key={statusId} value={statusId}>
                        {PRODUCTION_STATUS_CONFIG[statusId]?.label} (
                        {groupedOrders[statusId]?.length || 0})
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500">
                    <Filter size={14} />
                  </div>
                </div>
              </div>

              {/* TOPO */}
              <div className="bg-white border-b border-slate-200 p-4 shrink-0 space-y-4">
                <div className="w-full hidden sm:block">
                  <AgeChart
                    orders={baseFilteredOrders}
                    onBarClick={handleChartClick}
                    activeFilter={ageFilter}
                  />
                  {ageFilter && (
                    <div className="flex items-center justify-center mt-2">
                      <span className="bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full flex items-center gap-2">
                        Filtro Ativo: {ageFilter.label}
                        <button
                          onClick={() => setAgeFilter(null)}
                          className="hover:text-blue-900"
                        >
                          <CheckCircle size={12} />
                        </button>
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
                  <div className="flex flex-1 gap-2 w-full md:max-w-2xl">
                    {/* BUSCA */}
                    <div className="relative flex-1">
                      <Search
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                        size={16}
                      />
                      <input
                        type="text"
                        placeholder="Buscar SKU, Cliente, Pedido..."
                        className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm w-full focus:border-blue-500 outline-none shadow-sm"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                      />
                    </div>

                    {/* FILTRO DE TIPO (ESTOQUE, PE, ETC) */}
                    <div className="relative min-w-[170px]">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                        <Filter size={16} />
                      </div>
                      <select
                        className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500 appearance-none bg-white font-bold text-slate-700 shadow-sm"
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                      >
                        <option value="all">Todos os Tipos</option>
                        <option value="borracha">🟠 🏷️ Molde / Borracha ({borrachaCount})</option>
                        <option value="pe">⚡ INTERCEPTADOS</option>
                        <option value="stock">📦 Estoque Físico</option>
                        <option value="not_printed">📄 Pendente Impressão</option>
                        <option value="printed">🖨️ Já Impressos</option>
                        <option value="transit">🚚 Em Trânsito</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 w-full md:w-auto">
                    <div className="hidden sm:block text-xs font-bold text-slate-400 uppercase tracking-wide pt-2">
                      Modo Lista
                    </div>

                    {/* BARRA DE AÇÕES EM MASSA FLUTUANTE */}
                    {selectedOrders.size > 0 && (
                      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-slate-900 text-white px-6 py-3 rounded-full shadow-2xl z-50 flex items-center gap-6 animate-slide-up border border-slate-700">
                        <span className="font-bold text-sm bg-slate-700 px-3 py-1 rounded-full">
                          {selectedOrders.size} selecionados
                        </span>

                        <div className="h-6 w-px bg-slate-600"></div>

                        <div className="flex items-center bg-white/10 rounded-lg overflow-hidden h-8">
                          <select
                            className="pl-2 pr-4 py-1 text-sm bg-transparent outline-none cursor-pointer text-white font-bold h-full appearance-none hover:bg-white/20 transition-colors"
                            onChange={(e) => handleBatchMove(e.target.value)}
                            value=""
                          >
                            <option value="" disabled className="text-slate-800">
                              Mover para...
                            </option>
                            {KANBAN_ORDER.map((statusKey) => (
                              <option
                                key={statusKey}
                                value={statusKey}
                                className="text-slate-800"
                              >
                                {PRODUCTION_STATUS_CONFIG[statusKey]?.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="flex gap-2">
                          <button
                            onClick={() => handleBulkTransit("descendo")}
                            className="flex items-center gap-2 hover:text-orange-400 transition-colors group"
                            title="Enviar para Fábrica"
                          >
                            <ArrowDownCircle
                              size={24}
                              className="group-hover:animate-bounce"
                            />
                          </button>

                          <button
                            onClick={() => handleBulkTransit("subindo")}
                            className="flex items-center gap-2 hover:text-indigo-400 transition-colors group"
                            title="Enviar para Escritório"
                          >
                            <ArrowUpCircle
                              size={24}
                              className="group-hover:animate-bounce"
                            />
                          </button>
                        </div>

                        <div className="h-6 w-px bg-slate-600"></div>

                        <button
                          onClick={handleBatchPrint}
                          className="hover:text-blue-400 transition-colors"
                          title="Imprimir Selecionados"
                        >
                          <Printer size={20} />
                        </button>

                        <button
                          onClick={() => setSelectedOrders(new Set())}
                          className="ml-2 hover:bg-slate-700 p-1 rounded-full text-slate-400 hover:text-white"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* LISTA */}
              <div className="flex-1 overflow-y-auto p-2 md:p-4 custom-scrollbar bg-slate-50/50">
                <ProductionListView
                  user={user}
                  groupedOrders={memoizedGroupedOrdersForView}
                  selectedOrders={selectedOrders}
                  toggleSelect={toggleSelect}
                  setEditingOrder={setEditingOrder}
                  handleDeleteOrder={handleDeleteOrder}
                  handleMoveStatus={handleMoveStatus}
                  findCatalogItem={findCatalogItem}
                  onToggleTransit={handleToggleTransit}
                  onUpdateDate={handleUpdateDate}
                  setSelectSet={setSelectedOrders}
                  onTogglePE={handleTogglePE}
                />
              </div>
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
